import { Autocomplete, Stack, TextField } from '@mui/material';
import { PROFESSION_OPTIONS } from 'lib/consts/usersFields';
import {
  buildSignerValidation,
  isValidChileanPhone,
  isValidEmail,
  validaRut,
} from 'lib/functions/utils';
import React, { useMemo } from 'react';

import { SingleSelect } from '../../SingleSelect';
import { Aval, SignerInfo } from '../types';

type Props = {
  aval: Aval;
  setAval: (aval: Aval) => void;
  value: Aval[];
};

const Spouse: React.FC<Props> = ({ aval, setAval, value }) => {
  const relationOptions = useMemo(() => {
    const options = value
      .filter((a) => a.type === 'SAME_COMPANY')
      .map((a) => ({
        value: a.signer?.identifier ?? '',
        label: a.signer?.name ?? '',
      }));
    return options;
  }, [value]);

  const handleSelectRelation = (identifier: string) => {
    const avalRelation = value.find((a) => a.signer?.identifier === identifier);
    const spouseRelation = {
      name: avalRelation?.signer?.name ?? '',
      identifier: avalRelation?.signer?.identifier ?? '',
    };
    if (avalRelation) setAval({ ...aval, spouseRelation });
  };

  const handleAvalSignerChange = (field: keyof SignerInfo, value: string) => {
    setAval({ ...aval, signer: { ...aval?.signer, [field]: value } });
  };

  const { hasError: emailHasError, message: emailErrorMessage } =
    buildSignerValidation(
      !aval?.signer?.email?.length,
      !!aval?.signer?.email?.length && !isValidEmail(aval?.signer?.email),
      'Email obligatorio',
      'Email inválido',
    );

  const { hasError: nameHasError, message: nameErrorMessage } =
    buildSignerValidation(
      !aval?.signer?.name?.length,
      false,
      'Nombre obligatorio',
      'Nombre inválido',
    );

  const { hasError: identifierHasError, message: identifierErrorMessage } =
    buildSignerValidation(
      !aval?.signer?.identifier?.length,
      !!aval?.signer?.identifier?.length &&
        !validaRut(aval?.signer?.identifier),
      'RUT obligatorio',
      'RUT inválido',
    );

  const { hasError: phoneHasError, message: phoneErrorMessage } =
    buildSignerValidation(
      !aval?.signer?.phone?.length,
      !!aval?.signer?.phone?.length &&
        !isValidChileanPhone(aval?.signer?.phone),
      'Teléfono obligatorio',
      'Teléfono inválido (569XXXXXXXX)',
    );

  const { hasError: professionHasError, message: professionErrorMessage } =
    buildSignerValidation(
      !aval?.signer?.profession?.length,
      false,
      'Profesión obligatoria',
      'Profesión inválida',
    );

  return (
    <Stack direction="column" spacing={2}>
      <SingleSelect
        options={relationOptions}
        value={aval?.spouseRelation?.identifier ?? ''}
        onChange={handleSelectRelation}
        label="Relación"
      />
      <TextField
        label="Nombre"
        type="text"
        fullWidth
        value={aval?.signer?.name ?? ''}
        onChange={(e) => handleAvalSignerChange('name', e.target.value)}
        error={nameHasError}
        helperText={nameHasError ? nameErrorMessage : ''}
      />
      <TextField
        label="Email"
        type="text"
        fullWidth
        value={aval?.signer?.email ?? ''}
        onChange={(e) => handleAvalSignerChange('email', e.target.value)}
        error={emailHasError}
        helperText={emailHasError ? emailErrorMessage : ''}
      />
      <TextField
        label="Teléfono"
        type="text"
        fullWidth
        value={aval?.signer?.phone ?? ''}
        onChange={(e) => handleAvalSignerChange('phone', e.target.value)}
        error={phoneHasError}
        helperText={phoneHasError ? phoneErrorMessage : ''}
      />
      <TextField
        label="RUT"
        type="text"
        fullWidth
        value={aval?.signer?.identifier ?? ''}
        onChange={(e) => handleAvalSignerChange('identifier', e.target.value)}
        error={identifierHasError}
        helperText={identifierHasError ? identifierErrorMessage : ''}
      />
      <Autocomplete
        freeSolo
        options={PROFESSION_OPTIONS}
        value={aval?.signer?.profession ?? ''}
        onChange={(_event, newInputValue) =>
          handleAvalSignerChange('profession', newInputValue ?? '')
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Profesión"
            margin="dense"
            fullWidth
            onBlur={(e) => {
              const value = e.target.value;
              if (value) {
                handleAvalSignerChange('profession', value);
              }
            }}
            error={professionHasError}
            helperText={professionHasError ? professionErrorMessage : ''}
          />
        )}
      />
    </Stack>
  );
};

export default Spouse;
